import type { S3File } from "@dune/types";
import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@dune/ui";
import React, { useState } from "react";
import { toast } from "sonner";
import { registerS3File } from "../../api/queries/files.queries";
import { S3FileInput } from "./S3FileInput";

interface S3FileWithId extends S3File {
	id?: string;
}

interface TaskUploadDocumentModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onUpload: (fileId: string) => Promise<void>;
	isPending?: boolean;
}

const TaskUploadDocumentModal: React.FC<TaskUploadDocumentModalProps> = ({
	open,
	onOpenChange,
	onUpload,
	isPending = false,
}) => {
	const [selectedFile, setSelectedFile] = useState<S3FileWithId | null>(null);
	const [isUploading, setIsUploading] = useState(false);

	const handleFileUpload = async (file: S3File) => {
		setSelectedFile(file);

		try {
			setIsUploading(true);

			const registeredFile = await registerS3File({
				bucket: file.bucket,
				key: file.key,
				name: file.name,
				mimeType: file.type,
			});

			setSelectedFile({
				...file,
				id: registeredFile.id,
			});
		} catch (error) {
			console.error("File registration failed:", error);
			toast.error("Erreur lors de l'enregistrement du fichier");
			setSelectedFile(null);
		} finally {
			setIsUploading(false);
		}
	};

	const handleUpload = async () => {
		if (!selectedFile || !selectedFile.id) {
			toast.error("Veuillez sélectionner un fichier");
			return;
		}

		try {
			await onUpload(selectedFile.id);

			toast.success("Document ajouté à la tâche avec succès");
			onOpenChange(false);
			setSelectedFile(null);
		} catch (error) {
			console.error("Upload failed:", error);
			toast.error("Erreur lors de l'ajout du document");
		}
	};

	const handleModalClose = (newOpen: boolean) => {
		if (!newOpen) {
			setSelectedFile(null);
		}
		onOpenChange(newOpen);
	};

	return (
		<Dialog open={open} onOpenChange={handleModalClose}>
			<DialogContent className="sm:max-w-[500px]">
				<DialogHeader>
					<DialogTitle>Ajouter un document à la tâche</DialogTitle>
					<DialogDescription>
						Sélectionnez un fichier à associer à cette tâche
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					<div>
						<div className="text-sm font-medium mb-2">Fichier</div>
						<S3FileInput
							key={`task-upload-modal-${open}`}
							bucket="private"
							onUploadSuccess={handleFileUpload}
							hidePreviewButton={true}
							subText="Formats acceptés : PDF, DOCX, PNG, JPG (max. 10MB)"
						/>
					</div>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						Annuler
					</Button>
					<Button
						onClick={handleUpload}
						disabled={
							!selectedFile || isPending || isUploading || !selectedFile.id
						}
						isLoading={isPending || isUploading}
					>
						Ajouter le document
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default TaskUploadDocumentModal;
